import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import PageLoader from '../Components/PageLoader';
import TestAgentModal from '../Components/TestAgentModal';

import AgentSettings from '../Components/AgentSettings';
import { FetchItemsParams, FetchItemsResult } from '../Components/ItemsPickerModalTypes';

import { useAlert } from '../Components/AlertContext';
import { ConfirmationProvider } from '../Components/ConfirmationContext';
import { AgentApiProvider, useAgentApi } from '../Services/AgentApiProvider';

import { useAuth } from '../Contexts/AuthContext';
import { AssistantFrameApi, AssistantSwarmApi } from '../Services/AgentApiProviderTypes';
import { ArtifactApiProvider, useArtifactApi } from '../Services/ArtifactApiProvider';
import { isEmpty } from '../utils/validationUtil';
import {
  AgentConfig,
  AgentFeatureFlags,
  AgentPermissions,
  AgentReferenceData,
  configToPayload,
  EMPTY_DATA,
  getCollectionDisableReason,
  INITIAL_CONFIG,
} from './AgentHelpers';

function AgentContent({ canEdit }: { canEdit: boolean }) {
  const navigate = useNavigate();
  const { organizationId, agentId } = useParams<{ organizationId: string; agentId: string }>();
  const { showAlert } = useAlert();
  const { status } = useAuth();

  const isDocumentPermission = status?.permissions?.includes('all:document') ?? false;
  const isOrganizationsPermission = status?.permissions?.includes('all:organizations') ?? false;
  const isNew = agentId === 'new-agent';
  const isDisabled = !canEdit;

  const {
    getAgentById,
    getAgentNotifications,
    getAgentCatalog,
    getAgentFrames,
    getAgentMarkers,
    createAgent,
    updateAgentById,
    getPrompts,
    getNotifications,
    getCatalogs,
    getFramesPage,
    getMarkers,
    getDictionaries,
    getAllSwarm,
    getAgentSwarm,
    getDocumentCollections,
    getInformationPackageCollections,
    getConnections,
    getAssistantOptions,
  } = useAgentApi();

  const { getArtifact } = useArtifactApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Agent config (single object instead of 25 separate useState) ─

  const [config, setConfig] = useState<AgentConfig>(INITIAL_CONFIG);

  const updateConfig = useCallback(<K extends keyof AgentConfig>(key: K, value: AgentConfig[K]) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  }, []);

  // ── Reference / lookup data ───────────────────────────────────

  const [refData, setRefData] = useState<AgentReferenceData>(EMPTY_DATA);

  // ── Derived state (replaces 3 of the 4 original useEffects) ───

  const collectionDisableReason = useMemo(
    () => getCollectionDisableReason(config.agentType, config.skills, config.reasoning),
    [config.agentType, config.skills, config.reasoning],
  );
  const disableCollections = !!collectionDisableReason;
  const collectionTextWarning = collectionDisableReason ?? '';

  const isPromptRealtime = useMemo(() => {
    if (!config.promptId || !refData.prompts.length) return false;
    const selected = refData.prompts.find((p) => p.id === config.promptId);
    return selected?.pattern_type === 'realtime';
  }, [config.promptId, refData.prompts]);

  // Clear collections when they become disabled (keeps the original side-effect)
  useEffect(() => {
    if (disableCollections) {
      setConfig((prev) =>
        prev.documentCollectionIds.length > 0 ? { ...prev, documentCollectionIds: [] } : prev,
      );
    }
  }, [disableCollections]);

  const permissions: AgentPermissions = {
    isDisabled,
    isOrganizationsPermission,
    isDocumentPermission,
    disableCollections,
    collectionTextWarning,
  };

  const featureFlags: AgentFeatureFlags = {
    isDocumentFlag: status?.feature_flags?.DOCUMENT === true,
    isProductFlag: status?.feature_flags?.PRODUCT === true,
    isInformationPackageFlag: status?.feature_flags?.INFORMATION_PACKAGE === true,
    isNotificationFlag: status?.feature_flags?.NOTIFICATION === true,
    isConnectionFlag: status?.feature_flags?.CONNECTION === true,
  };

  // ── Frames picker paging ──────────────────────────────────────
  // ItemsPickerModal pages with an opaque cursor; the frames endpoint uses
  // skip/limit, so the cursor is just the next skip offset.
  const fetchFrames = useCallback(
    async ({
      limit,
      pageToken,
      searchQuery,
    }: FetchItemsParams): Promise<FetchItemsResult<AssistantFrameApi>> => {
      const skip = pageToken ? Number(pageToken) : 0;
      const response = await getFramesPage(skip, limit, searchQuery);
      const items = response.items ?? [];
      const nextSkip = skip + items.length;
      const hasMore = items.length > 0 && nextSkip < (response.totalCount ?? 0);
      return {
        items,
        nextPageToken: hasMore ? String(nextSkip) : null,
        hasMore,
        totalCount: response.totalCount ?? undefined,
      };
    },
    [getFramesPage],
  );

  // ── Data fetch ────────────────────────────────────────────────

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        // The agent's own frames double as the picker's pre-selected items, so
        // a selection is never lost just because its row sits on a later page.
        let agentFrames: AssistantFrameApi[] = [];

        if (!isNew && agentId) {
          const [agent, notifs, frames, markers, swarms, catalog] = await Promise.all([
            getAgentById(agentId),
            getAgentNotifications(agentId),
            getAgentFrames(agentId),
            getAgentMarkers(agentId),
            getAgentSwarm(agentId),
            isDocumentPermission ? getAgentCatalog(agentId) : Promise.resolve(null),
          ]);

          agentFrames = frames ?? [];

          setConfig({
            name: agent.name,
            isActive: agent.is_active,
            agentType: agent.assistant_type,
            modelName: agent.model_name || null,
            reasoning: agent.reasoning || null,
            temperature: agent.temperature,
            detailsLevel: agent.details_level,
            promptId: agent.prompt_id,
            selectedVoice: agent.voice ?? 'cedar',
            memoryType: agent.memory_type,
            memoryLimit: agent.memory_limit,
            skills: agent.skills ?? [],
            frameIds: frames?.map((f: { id: string }) => f.id) ?? [],
            markerIds: markers?.map((m: { id: string }) => m.id) ?? [],
            swarmKeys: swarms?.map((s: { key: string }) => s.key) ?? [],
            notificationIds: notifs?.map((n: { id: string }) => n.id) ?? [],
            documentCollectionIds: agent.document_collection_keys ?? [],
            informationPackageCollectionIds: agent.information_package_collection_keys ?? [],
            connectionIds: agent.connections ?? [],
            catalog: catalog?.id ?? null,
            dictionaryId: agent.taxonomy_dictionary_id ?? null,
            templateIds: agent.templates ?? [],
            auditExecution: agent.audit_execution ?? true,
          });
        }

        const [
          prompts,
          notifications,
          documentCollections,
          informationPackageCollections,
          connections,
          allMarkers,
          dictionaries,
          allSwarms,
          artifactData,
          catalogs,
          options,
        ] = await Promise.all([
          getPrompts(),
          getNotifications(),
          getDocumentCollections(),
          getInformationPackageCollections(),
          getConnections(),
          getMarkers(),
          getDictionaries(),
          getAllSwarm().then((s: AssistantSwarmApi[]) => s || []),
          getArtifact(0, 1000, 'template'),
          isDocumentPermission ? getCatalogs() : Promise.resolve([]),
          getAssistantOptions(),
        ]);

        setRefData({
          prompts,
          notifications,
          documentCollections,
          informationPackageCollections,
          connections,
          selectedFrames: agentFrames,
          markers: allMarkers,
          dictionaries,
          swarms: allSwarms,
          templates: artifactData.items,
          catalogs,
          options,
        });
      } catch (err) {
        console.error('Error fetching agent data:', err);
        setError('Failed to load agent data');
      } finally {
        setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, isDocumentPermission]);

  // ── Save ──────────────────────────────────────────────────────

  const onSave = async () => {
    if (isEmpty(config.name)) {
      showAlert('fail', 'Please enter agent name');
      return;
    }
    if (isEmpty(config.detailsLevel)) {
      showAlert('fail', 'Please select level of details');
      return;
    }
    if (isEmpty(config.promptId)) {
      showAlert('fail', 'Please select prompt');
      return;
    }
    if (isEmpty(config.agentType)) {
      showAlert('fail', 'Please select agent type');
      return;
    }
    if (disableCollections && config.documentCollectionIds?.length > 0) {
      showAlert('fail', `Cannot save agent: ${collectionTextWarning}`);
      return;
    }
    if (
      config.agentType === 'RAG' &&
      config.documentCollectionIds?.length > 0 &&
      isEmpty(config.reasoning)
    ) {
      showAlert('fail', 'Please select reasoning');
      return;
    }

    setLoading(true);
    try {
      const payload = configToPayload(config);

      if (isNew) {
        const result = await createAgent(payload);
        showAlert('success', 'Agent created successfully');
        navigate(`/organizations/${organizationId}/agents-hub/${result.key}`, {
          state: { organizationId },
        });
      } else {
        await updateAgentById(agentId, payload);
        showAlert('success', 'Agent updated successfully');
      }
    } catch (err) {
      console.error('Error saving agent data:', err);
      showAlert('fail', 'Failed to save agent data');
    } finally {
      setLoading(false);
    }
  };

  const onCancel = () => {
    navigate(`/organizations/${organizationId}/agents-hub`, { state: { organizationId } });
  };

  if (loading) return <PageLoader />;

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-6">
          <div className="d-flex justify-between align-center">
            <h2 className="h2-title m-0">{config.name}</h2>
            {!isNew && (
              <div className="buttons-container d-flex justify-end">
                <Button
                  variant="secondary"
                  size="large"
                  onClick={() =>
                    navigate(
                      `/organizations/${organizationId}/agents-hub/${agentId}/audit-operations`,
                    )
                  }
                >
                  Audit Operations
                </Button>
                <Button
                  variant="secondary"
                  size="large"
                  onClick={() =>
                    navigate(`/organizations/${organizationId}/agents-hub/${agentId}/instructions`)
                  }
                >
                  View Instructions
                </Button>
              </div>
            )}
          </div>

          <div className="gap d-flex flex-row-to-col flex-column">
            <div className="int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr">
              <AgentSettings
                config={config}
                data={refData}
                permissions={permissions}
                featureFlags={featureFlags}
                onChange={updateConfig}
                fetchFrames={fetchFrames}
              />
            </div>
          </div>

          <div className="buttons-container d-flex justify-end">
            <Button variant="primary" size="medium" disabled={isDisabled} onClick={onSave}>
              Save
            </Button>
            <Button variant="danger" size="medium" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>

        {!isNew && (
          <div className="radius-yes p-2 bg-yes radius col-6">
            <h2 className="h2-title">Test Agent</h2>
            <div className="gap d-flex border-yes radius-yes flex-row-to-col flex-column border-yes">
              <TestAgentModal isOpen isPromptRealtime={isPromptRealtime} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Agent({ canEdit }: { canEdit: boolean }) {
  return (
    <ConfirmationProvider>
      <AgentApiProvider>
        <ArtifactApiProvider>
          <AgentContent canEdit={canEdit} />
        </ArtifactApiProvider>
      </AgentApiProvider>
    </ConfirmationProvider>
  );
}

export default Agent;
